import { useState, useMemo, type FormEvent } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { GlassInput } from '@/components/ui/GlassInput';
import { GlassButton } from '@/components/ui/GlassButton';
import { useToast } from '@/components/ui/GlassToast';
import api from '@/lib/api';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get('token') ?? '', [params]);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const toast = useToast();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) {
      toast.addToast({ type: 'error', title: 'Missing token', description: 'Open the link from your email.' });
      return;
    }
    if (password.length < 8) {
      toast.addToast({ type: 'error', title: 'Password too short', description: 'Use at least 8 characters.' });
      return;
    }
    if (password !== confirm) {
      toast.addToast({ type: 'error', title: 'Passwords don\'t match', description: 'Re-enter the same password twice.' });
      return;
    }

    setIsSubmitting(true);
    try {
      await api.auth.resetPassword({ token, password });
      setDone(true);
      toast.addToast({ type: 'success', title: 'Password reset', description: 'Sign in with your new password.' });
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      toast.addToast({
        type: 'error',
        title: 'Reset failed',
        description: err instanceof Error ? err.message : 'Token may be expired. Request a new link.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full bg-[var(--color-brand-600)] opacity-20 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[400px] h-[400px] rounded-full bg-purple-600 opacity-15 blur-[100px] pointer-events-none" />

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[var(--color-brand-500)] to-purple-600 shadow-lg shadow-[var(--color-brand-500)]/25 mb-4">
            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gradient-brand">Choose a new password</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            {token ? 'Enter your new password below.' : 'This reset link is missing its token.'}
          </p>
        </div>

        <div className="glass-card p-6 sm:p-8">
          {done ? (
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-500/30">
                <svg className="w-6 h-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm text-[var(--text-secondary)]">
                Password updated. Redirecting you to sign in…
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <GlassInput
                type="password"
                label="New password"
                placeholder="Minimum 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                disabled={!token}
              />
              <GlassInput
                type="password"
                label="Confirm new password"
                placeholder="Re-enter password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
                disabled={!token}
              />

              <GlassButton
                type="submit"
                loading={isSubmitting}
                disabled={!token}
                className="w-full"
                size="lg"
              >
                Reset password
              </GlassButton>

              <div className="text-center">
                <Link
                  to="/forgot-password"
                  className="text-sm text-[var(--color-brand-400)] hover:text-[var(--color-brand-300)] transition-colors"
                >
                  Request a new link
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
